import React, { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  EXPLORE_FEED,
  ACTIVITY_LABEL,
  ExploreItem,
  TRENDS,
  TREND_LEADERBOARDS,
  TikTokTrend,
  formatTime,
  formatPlays,
  timeRemaining,
  activityFamily,
} from "../mockData";
import { ActivityFilter, AngleFilter, FilterCard } from "../components/VisualFilter";
import { VideoEmbed, PlatformBadge } from "../components/VideoEmbed";
import {
  Search, Heart, Eye, Sparkles, Music2, TrendingUp, Clock, Crown,
} from "lucide-react";

type Tab = "feed" | "trends" | "search";

function tabFromPath(pathname: string): Tab {
  if (pathname.endsWith("/trends")) return "trends";
  if (pathname.endsWith("/search")) return "search";
  return "feed";
}

export default function Explore() {
  const loc = useLocation();
  const tab = tabFromPath(loc.pathname);

  return (
    <div className="space-y-4">
      {tab === "feed"   && <Feed />}
      {tab === "trends" && <Trends />}
      {tab === "search" && <SearchView />}
    </div>
  );
}

/* ---------- Feed (mixed: trending + curated) ---------- */

function Feed() {
  const [activity, setActivity] = useState<string>("all");
  const [show, setShow] = useState<"all" | "trends" | "picks">("all");

  const curated = useMemo(
    () => [...EXPLORE_FEED].sort((a, b) => b.score - a.score).slice(0, 18),
    []
  );
  const hot = useMemo(
    () => [...TRENDS].sort((a, b) => b.plays - a.plays),
    []
  );

  const filteredHot = useMemo(
    () =>
      hot.filter((t) =>
        activity === "all" ? true : activityFamily(t.activityId) === activity
      ),
    [hot, activity]
  );
  const filteredPicks = useMemo(
    () =>
      curated.filter((p) =>
        activity === "all" ? true : activityFamily(p.activity) === activity
      ),
    [curated, activity]
  );

  // Sprinkle trends evenly through the picks (picks-first, ~3 picks per trend),
  // so masonry columns don't all start with a trend.
  const mixed = useMemo(() => {
    type Row = { kind: "trend"; data: TikTokTrend } | { kind: "pick"; data: ExploreItem };
    if (show === "trends") return filteredHot.map<Row>((t) => ({ kind: "trend", data: t }));
    if (show === "picks")  return filteredPicks.map<Row>((p) => ({ kind: "pick", data: p }));

    const out: Row[] = [];
    let ti = 0, ci = 0;
    while (ci < filteredPicks.length || ti < filteredHot.length) {
      // 3 picks
      for (let k = 0; k < 3 && ci < filteredPicks.length; k++) {
        out.push({ kind: "pick", data: filteredPicks[ci++] });
      }
      // 1 trend
      if (ti < filteredHot.length) {
        out.push({ kind: "trend", data: filteredHot[ti++] });
      }
    }
    return out;
  }, [filteredHot, filteredPicks, show]);

  const activeCount = (activity !== "all" ? 1 : 0) + (show !== "all" ? 1 : 0);

  return (
    <div className="space-y-4">
      <FilterCard
        countActive={activeCount}
        summary={[
          { label: "Activity", value: activity === "all" ? "All" : activity,
            onClear: activity !== "all" ? () => setActivity("all") : undefined },
          { label: "Show", value: show === "all" ? "Trends + picks" : show === "trends" ? "Trends only" : "Picks only",
            onClear: show !== "all" ? () => setShow("all") : undefined },
        ]}
      >
        <ActivityFilter value={activity} onChange={setActivity} />
        <div>
          <div className="label-eyebrow mb-2">Show</div>
          <div className="flex flex-wrap gap-1.5">
            {([
              ["all", "Trends + picks"],
              ["trends", "Trends only"],
              ["picks", "Picks only"],
            ] as const).map(([k, label]) => (
              <button
                key={k}
                onClick={() => setShow(k)}
                className={[
                  "h-8 rounded-md px-3 text-xs font-medium transition",
                  show === k
                    ? "bg-el-50 text-el-700 ring-1 ring-el-200"
                    : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50",
                ].join(" ")}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </FilterCard>

      <div className="flex items-center justify-between gap-3 px-1">
        <div className="text-xs text-slate-500">
          {mixed.length} items · {filteredHot.length} trends · {filteredPicks.length} curated
        </div>
        <div className="text-[0.7rem] text-slate-500 inline-flex items-center gap-1.5">
          <TrendingUp className="h-3 w-3 text-el-500" />
          For you
        </div>
      </div>

      {mixed.length === 0 ? (
        <div className="card p-12 text-center text-sm text-slate-500">
          Nothing matches these filters yet.
        </div>
      ) : (
        <div className="columns-1 sm:columns-2 xl:columns-3 2xl:columns-4 gap-4 [column-fill:_balance]">
          {mixed.map((row, i) => {
            const key = row.kind === "trend"
              ? `t-${row.data.id}-${i}`
              : `p-${row.data.id}-${i}`;
            return (
              <div key={key} className="mb-4 break-inside-avoid">
                {row.kind === "trend"
                  ? <TrendCard trend={row.data} compact />
                  : <FeedCard item={row.data} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------- Search (filter all public analyses) ---------- */

function SearchView() {
  const [activity, setActivity] = useState<string>("all");
  const [angle, setAngle] = useState<string>("all");
  const [minScore, setMinScore] = useState<number>(0);
  const [maxScore, setMaxScore] = useState<number>(100);
  const [q, setQ] = useState<string>("");

  const filtered = useMemo(() => {
    return EXPLORE_FEED.filter((it) => {
      if (activity !== "all" && activityFamily(it.activity) !== activity) return false;
      if (angle !== "all" && it.angle !== angle) return false;
      if (it.score < minScore || it.score > maxScore) return false;
      if (q && !it.athlete.toLowerCase().includes(q.toLowerCase()) &&
              !it.caption.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [activity, angle, minScore, maxScore, q]);

  const top = [...EXPLORE_FEED].sort((a, b) => b.score - a.score).slice(0, 3);

  return (
    <div className="space-y-4">
      <FilterCard
        countActive={(activity !== "all" ? 1 : 0) + (angle !== "all" ? 1 : 0) + (minScore > 0 || maxScore < 100 ? 1 : 0) + (q ? 1 : 0)}
        search={
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by athlete or caption…"
              className="w-full h-9 pl-9 pr-3 text-sm rounded-md bg-white border border-slate-200 focus:outline-none focus:shadow-ring"
            />
          </div>
        }
        summary={[
          { label: "Activity", value: activity === "all" ? "All" : activity,
            onClear: activity !== "all" ? () => setActivity("all") : undefined },
          { label: "Angle", value: angle === "all" ? "All" : angle,
            onClear: angle !== "all" ? () => setAngle("all") : undefined },
          { label: "Score", value: `${minScore} – ${maxScore}`,
            onClear: (minScore > 0 || maxScore < 100)
              ? () => { setMinScore(0); setMaxScore(100); }
              : undefined },
        ]}
      >
        <ActivityFilter value={activity} onChange={setActivity} />
        <AngleFilter value={angle} onChange={setAngle} />
        <ScoreRange min={minScore} max={maxScore}
          onChange={(lo, hi) => { setMinScore(lo); setMaxScore(hi); }} />
      </FilterCard>

      <div className="flex items-center justify-between gap-3 px-1">
        <div className="text-xs text-slate-500">
          {filtered.length} of {EXPLORE_FEED.length} public analyses
        </div>
        <div className="text-[0.7rem] text-slate-500 inline-flex items-center gap-1.5">
          <Sparkles className="h-3 w-3 text-el-500" />
          AI-scored automatically
        </div>
      </div>

      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-slate-900">Top this week</div>
          <div className="text-[0.7rem] text-slate-500">Highest-scoring public analyses</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {top.map((t) => (
            <TopCard key={t.id} item={t} />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((it) => (
          <FeedCard key={it.id} item={it} />
        ))}
        {filtered.length === 0 && (
          <div className="card md:col-span-2 xl:col-span-3 p-12 text-center text-sm text-slate-500">
            No public analyses match your filters.
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Trends (TikTok-style dance challenges) ---------- */

type TrendSort = "plays" | "newest" | "closing";

function Trends() {
  const [q, setQ] = useState("");
  const [activity, setActivity] = useState<string>("all");
  const [sort, setSort] = useState<TrendSort>("plays");

  const filtered = useMemo(() => {
    const needle = q.toLowerCase();
    let list = TRENDS.filter((t) => {
      if (activity !== "all" && activityFamily(t.activityId) !== activity) return false;
      if (!needle) return true;
      return (
        t.title.toLowerCase().includes(needle) ||
        t.song.toLowerCase().includes(needle) ||
        t.artist.toLowerCase().includes(needle) ||
        t.hashtag.toLowerCase().includes(needle)
      );
    });
    list = [...list].sort((a, b) => {
      if (sort === "plays")   return b.plays - a.plays;
      if (sort === "newest")  return new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime();
      return new Date(a.endsAt).getTime() - new Date(b.endsAt).getTime();
    });
    return list;
  }, [q, activity, sort]);

  const activeCount = (q ? 1 : 0) + (activity !== "all" ? 1 : 0) + (sort !== "plays" ? 1 : 0);

  return (
    <div className="space-y-4">
      <FilterCard
        countActive={activeCount}
        search={
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by trend, song, artist or hashtag…"
              className="w-full h-9 pl-9 pr-3 text-sm rounded-md bg-white border border-slate-200 focus:outline-none focus:shadow-ring"
            />
          </div>
        }
        summary={[
          { label: "Activity", value: activity === "all" ? "All" : activity,
            onClear: activity !== "all" ? () => setActivity("all") : undefined },
          { label: "Sort",     value: sort === "plays" ? "Most plays" : sort === "newest" ? "Newest" : "Closing soon",
            onClear: sort !== "plays" ? () => setSort("plays") : undefined },
        ]}
      >
        <ActivityFilter value={activity} onChange={setActivity} />
        <div>
          <div className="label-eyebrow mb-2">Sort</div>
          <div className="flex flex-wrap gap-1.5">
            {([
              ["plays",   "Most plays"],
              ["newest",  "Newest"],
              ["closing", "Closing soon"],
            ] as const).map(([k, label]) => (
              <button
                key={k}
                onClick={() => setSort(k)}
                className={[
                  "h-8 rounded-md px-3 text-xs font-medium transition",
                  sort === k
                    ? "bg-el-50 text-el-700 ring-1 ring-el-200"
                    : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50",
                ].join(" ")}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </FilterCard>

      <div className="flex items-center justify-between gap-3 px-1">
        <div className="text-xs text-slate-500">
          {filtered.length} of {TRENDS.length} active trends · global leaderboards
        </div>
        <div className="text-[0.7rem] text-slate-500 inline-flex items-center gap-1.5">
          <TrendingUp className="h-3 w-3 text-el-500" />
          {sort === "plays" ? "Most plays first" : sort === "newest" ? "Newest first" : "Closing soonest"}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filtered.map((t) => (
          <TrendCard key={t.id} trend={t} />
        ))}
        {filtered.length === 0 && (
          <div className="card lg:col-span-2 p-12 text-center text-sm text-slate-500">
            No trends match your filters.
          </div>
        )}
      </div>
    </div>
  );
}

export function TrendCard({
  trend,
  compact = false,
}: {
  trend: TikTokTrend;
  /** Compact variant for masonry/listing where the leaderboard preview is too heavy. */
  compact?: boolean;
}) {
  const board = TREND_LEADERBOARDS[trend.id] ?? [];
  const top3 = board.slice(0, 3);
  const closing = timeRemaining(trend.endsAt);

  // Prefer the #1 entry's actual sourced video; fall back to the trend's reference clip.
  const cardVideo = board[0]?.video ?? trend.referenceVideo;

  return (
    <div className="card p-0 overflow-hidden hover:ring-1 hover:ring-el-200 transition group block">
      {/* Embed bar — pills above so they don't overlap the player */}
      <div className="px-4 pt-3 pb-2 flex items-center gap-2 flex-wrap">
        <span className="text-[0.65rem] uppercase tracking-[0.08em] font-semibold text-slate-700 bg-slate-100 rounded px-2 py-0.5">
          {trend.hashtag}
        </span>
        <span className="text-[0.65rem] uppercase tracking-[0.08em] font-semibold text-slate-700 bg-slate-100 rounded px-2 py-0.5">
          {trend.bpm} BPM
        </span>
        {cardVideo && <PlatformBadge platform={cardVideo.platform} />}
        <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-slate-900/85 text-white text-[0.65rem] font-semibold px-2 py-0.5">
          <Clock className="h-3 w-3" />
          {closing}
        </span>
      </div>

      {/* Cover — actual sourced video */}
      <div className="px-4">
        {cardVideo ? (
          <VideoEmbed video={cardVideo} title={`${trend.title} — top entry`} size="compact" />
        ) : (
          <div className="aspect-[16/8] bg-gradient-to-br from-fuchsia-500 via-rose-400 to-amber-300 flex items-center justify-center text-7xl rounded-xl">
            <span className="drop-shadow">{trend.emoji}</span>
          </div>
        )}
      </div>

      <Link to={`/explore/trend/${trend.id}`} className="block p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-base font-semibold text-slate-900 group-hover:text-el-700 truncate">
              {trend.title}
            </div>
            <div className="text-xs text-slate-500 truncate inline-flex items-center gap-1.5">
              <Music2 className="h-3 w-3" />
              {trend.song} · {trend.artist}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-sm font-semibold text-slate-900 tabular-nums">
              {formatPlays(trend.plays)}
            </div>
            <div className="text-[0.65rem] text-slate-500">global plays</div>
          </div>
        </div>

        {!compact && (
          <div className="mt-3 border-t border-slate-100 pt-3">
            <div className="flex items-center gap-1.5 mb-2">
              <Crown className="h-3.5 w-3.5 text-el-600" />
              <div className="text-[0.7rem] uppercase tracking-[0.06em] font-semibold text-slate-500">
                Top of the leaderboard
              </div>
            </div>
            <ul className="space-y-1.5">
              {top3.map((e) => (
                <li key={e.rank} className="flex items-center gap-2.5 text-xs">
                  <span className={[
                    "w-5 text-center font-semibold tabular-nums",
                    e.rank === 1 ? "text-amber-600" : e.rank === 2 ? "text-slate-500" : "text-amber-700/70",
                  ].join(" ")}>
                    {e.rank}
                  </span>
                  <div className="h-6 w-6 rounded-full bg-gradient-to-br from-el-200 to-el-400 text-white text-[0.6rem] font-semibold flex items-center justify-center">
                    {e.initials}
                  </div>
                  <span className="text-base leading-none">{e.flag}</span>
                  <span className="flex-1 truncate text-slate-700">{e.user}</span>
                  <span className="font-semibold text-slate-900 tabular-nums">{e.score}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Link>
    </div>
  );
}

function TopCard({ item }: { item: ExploreItem }) {
  return (
    <div className="rounded-md bg-white ring-1 ring-slate-200 overflow-hidden">
      <div className="aspect-video bg-slate-900 relative flex items-center justify-center text-3xl">
        <span className="opacity-90">{item.thumb}</span>
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        <div className="absolute top-2 left-2 flex gap-1">
          <Pill>{item.angle}</Pill>
          <Pill>{ACTIVITY_LABEL[item.activity].split(" — ")[0]}</Pill>
        </div>
        <div className="absolute bottom-2 right-2 text-white text-xs font-semibold bg-el-500/95 rounded px-2 py-0.5">
          {item.score}
        </div>
      </div>
      <div className="p-3">
        <div className="text-xs font-medium text-slate-900 truncate">{item.athlete}</div>
        <div className="text-[0.7rem] text-slate-500 truncate">{item.org}</div>
      </div>
    </div>
  );
}

export function FeedCard({ item }: { item: ExploreItem }) {
  return (
    <div className="card p-0 overflow-hidden hover:ring-1 hover:ring-el-200 transition">
      <div className="aspect-video bg-slate-900 relative flex items-center justify-center text-5xl">
        <span className="opacity-90">{item.thumb}</span>
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        <div className="absolute top-2 left-2 flex gap-1">
          <Pill>{item.angle}</Pill>
          <Pill>{ACTIVITY_LABEL[item.activity]}</Pill>
        </div>
        <div className="absolute top-2 right-2 text-white text-xs font-semibold bg-el-500/95 rounded px-2 py-0.5">
          {item.score}
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-el-200 to-el-400 text-white text-xs font-semibold flex items-center justify-center">
            {item.initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-900 truncate">{item.athlete}</div>
            <div className="text-[0.7rem] text-slate-500 truncate">{item.org} · {formatTime(item.capturedAt)}</div>
          </div>
        </div>
        <p className="mt-2 text-sm text-slate-700 line-clamp-2">{item.caption}</p>
        <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1"><Heart className="h-3.5 w-3.5" /> {item.cheers}</span>
          <span className="inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> {item.views}</span>
          <button className="ml-auto btn-secondary !h-8 !text-xs">Watch</button>
        </div>
      </div>
    </div>
  );
}

function ScoreRange({
  min, max, onChange,
}: {
  min: number; max: number;
  onChange: (lo: number, hi: number) => void;
}) {
  const FULL_MIN = 0, FULL_MAX = 100;
  const loPct = ((min - FULL_MIN) / (FULL_MAX - FULL_MIN)) * 100;
  const hiPct = ((max - FULL_MIN) / (FULL_MAX - FULL_MIN)) * 100;

  return (
    <div>
      <div className="flex items-baseline gap-2 mb-2">
        <span className="label-eyebrow">Score range</span>
        <span className="text-[0.7rem] text-slate-500 tabular-nums">{min} – {max}</span>
      </div>
      <div className="relative h-9">
        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-slate-100" />
        <div
          className="absolute top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-gradient-to-r from-el-400 to-el-600"
          style={{ left: `${loPct}%`, right: `${100 - hiPct}%` }}
        />
        <input
          type="range" min={FULL_MIN} max={FULL_MAX} step={5}
          value={min}
          onChange={(e) => {
            const v = Number(e.target.value);
            onChange(Math.min(v, max - 5), max);
          }}
          className="range-thumb absolute inset-0 w-full bg-transparent appearance-none pointer-events-none"
          style={{ zIndex: 3 }}
        />
        <input
          type="range" min={FULL_MIN} max={FULL_MAX} step={5}
          value={max}
          onChange={(e) => {
            const v = Number(e.target.value);
            onChange(min, Math.max(v, min + 5));
          }}
          className="range-thumb absolute inset-0 w-full bg-transparent appearance-none pointer-events-none"
          style={{ zIndex: 4 }}
        />
      </div>
      <div className="flex justify-between text-[0.65rem] text-slate-400 tabular-nums mt-0.5">
        <span>0</span><span>25</span><span>50</span><span>75</span><span>100</span>
      </div>
    </div>
  );
}

function Pill({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[0.6rem] uppercase tracking-[0.08em] font-semibold text-white/95 bg-black/45 backdrop-blur-sm rounded px-1.5 py-0.5">
      {children}
    </span>
  );
}
